<p-card
  class="block h-full overflow-hidden rounded-xl border border-surface-switch-300 bg-surface-switch-0"
>
  <ng-template #header>
    <div class="p-5 pt-4 pb-0">
      <div class="font-semibold text-xl">Revisions</div>
      <p class="mt-1 mb-0 text-sm text-muted-color">
        @if (approvalFlowEnabled()) {
          Saved snapshots of this quote. Send the current revision for review,
          view any revision, or restore a published revision as a new draft.
        } @else {
          Saved snapshots of this quote. Publish the current draft, view any
          revision, or restore a published revision as a new draft.
        }
      </p>
    </div>
  </ng-template>

  @if (revisions().length > 0) {
    <ul
      class="m-0 flex max-h-96 list-none flex-col overflow-y-auto rounded-lg border border-surface-switch-200 p-0"
    >
      @for (revision of revisions(); track revision.id) {
        @let isViewing = revision.number === viewingRevisionNumber();
        <li
          class="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-surface-switch-200 px-5 py-4 last:border-b-0"
          [class.bg-surface-switch-100]="isViewing"
          [attr.aria-current]="isViewing ? 'true' : null"
        >
          <div class="flex min-w-0 flex-1 basis-72 flex-col gap-1">
            <div class="flex flex-wrap items-center gap-2 text-sm font-medium">
              @if (editingRevisionId() === revision.id) {
                <input
                  #nameInput
                  pInputText
                  pSize="small"
                  type="text"
                  class="min-w-0 flex-1"
                  aria-label="Revision name"
                  [attr.maxlength]="maxNameLength"
                  [value]="draftName()"
                  [disabled]="isBusy()"
                  (input)="onDraftInput($event)"
                  (keydown.enter)="saveName(revision)"
                  (keydown.escape)="cancelEdit()"
                />
                <p-button
                  severity="secondary"
                  [text]="true"
                  size="small"
                  icon="pi pi-check"
                  ariaLabel="Save revision name"
                  pTooltip="Save the new name (Enter)."
                  tooltipPosition="top"
                  [disabled]="isBusy()"
                  (onClick)="saveName(revision)"
                />
                <p-button
                  severity="secondary"
                  [text]="true"
                  size="small"
                  icon="pi pi-times"
                  ariaLabel="Cancel renaming"
                  pTooltip="Keep the current name (Esc)."
                  tooltipPosition="top"
                  [disabled]="isBusy()"
                  (onClick)="cancelEdit()"
                />
              } @else {
                <span class="break-words" data-testid="revision-name">{{
                  displayName(revision)
                }}</span>
                <p-button
                  severity="secondary"
                  [text]="true"
                  size="small"
                  icon="pi pi-pencil"
                  [ariaLabel]="'Rename revision ' + displayName(revision)"
                  pTooltip="Rename this revision. The name is only a label; pricing is not affected."
                  tooltipPosition="top"
                  [disabled]="isBusy()"
                  (onClick)="startEdit(revision)"
                />
              }
              @if (isViewing) {
                <span
                  class="inline-flex items-center rounded border border-primary/40 px-1.5 py-0.5 text-xs font-semibold text-primary"
                  >Currently Viewing</span
                >
              }
              @if (approvalFlowEnabled()) {
                @switch (revision.approval_status) {
                  @case ("REQUESTED") {
                    <p-tag severity="info" value="Pending review" />
                  }
                  @case ("CHANGES_REQUESTED") {
                    <p-tag severity="warn" value="Changes requested" />
                  }
                }
              }
            </div>
            <div class="text-xs text-muted-color">
              Created {{ revision.created_date | date: "d MMM, y HH:mm" }}
              ·
              @if (revision.published_at) {
                Published {{ revision.published_at | date: "d MMM, y HH:mm" }}
              } @else {
                <span class="italic">Not published</span>
              }
              ·
              <span
                class="font-semibold tabular-nums text-surface-switch-900"
                data-testid="revision-total"
                >Total:
                {{ revision.total | currency: revision.currency_code }}</span
              >
            </div>
          </div>
          <div
            class="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2"
          >
            @if (isViewing && revision.id === currentRevisionId()) {
              @if (canPublish(revision)) {
                <p-button
                  icon="pi pi-file-check"
                  label="Publish"
                  [disabled]="isBusy()"
                  [ariaLabel]="'Publish revision ' + displayName(revision)"
                  pTooltip="Publish this draft. Editing it afterwards starts a new revision."
                  tooltipPosition="top"
                  (onClick)="publish(revision)"
                />
              }
              @if (canRequestReview(revision)) {
                <p-button
                  icon="pi pi-send"
                  [label]="
                    revision.approval_status === 'CHANGES_REQUESTED'
                      ? 'Re-request review'
                      : 'Request review'
                  "
                  [disabled]="isBusy()"
                  [ariaLabel]="'Request review for ' + displayName(revision)"
                  [pTooltip]="
                    revision.approval_status === 'CHANGES_REQUESTED'
                      ? 'Send the updated revision back to an approver for another review.'
                      : 'Send this revision to an approver. It is published once approved.'
                  "
                  tooltipPosition="top"
                  (onClick)="requestReview(revision)"
                />
              }
              @if (canReview(revision)) {
                <p-button
                  severity="success"
                  icon="pi pi-check"
                  label="Approve"
                  [disabled]="isBusy()"
                  [ariaLabel]="'Approve and publish ' + displayName(revision)"
                  pTooltip="Approve this revision and publish it."
                  tooltipPosition="top"
                  (onClick)="approve(revision)"
                />
                <p-button
                  severity="warn"
                  [outlined]="true"
                  icon="pi pi-pencil"
                  label="Request Changes"
                  [disabled]="isBusy()"
                  [ariaLabel]="'Request changes to ' + displayName(revision)"
                  pTooltip="Send this revision back to its author with feedback. It stays unpublished."
                  tooltipPosition="top"
                  (onClick)="openRequestChanges(revision)"
                />
              }
            }
            @if (!isViewing) {
              <p-button
                severity="secondary"
                icon="pi pi-eye"
                label="View"
                [ariaLabel]="
                  'View revision ' + quoteCode() + '-' + revision.number
                "
                pTooltip="Open this revision to review its items and pricing."
                tooltipPosition="top"
                (onClick)="quoteService.loadRevision(revision.number)"
              />
            } @else if (isRestorable(revision)) {
              <span
                [pTooltip]="
                  hasCurrentDraft()
                    ? 'Create a new draft with the items, prices and currency of this revision. To restore, publish or discard the current draft first.'
                    : 'Create a new draft with the items, prices and currency of this revision. Existing revisions are kept.'
                "
                tooltipPosition="top"
              >
                <p-button
                  severity="secondary"
                  [outlined]="true"
                  icon="pi pi-replay"
                  label="Restore"
                  [disabled]="hasCurrentDraft() || isBusy()"
                  [ariaLabel]="
                    'Restore revision ' + quoteCode() + '-' + revision.number
                  "
                  (onClick)="confirmRestore(revision)"
                />
              </span>
            } @else if (
              revision.id === currentRevisionId() && canDiscardCurrentDraft()
            ) {
              <p-button
                severity="danger"
                [outlined]="true"
                icon="pi pi-trash"
                label="Discard"
                [disabled]="isBusy()"
                [ariaLabel]="
                  'Discard draft revision ' +
                  quoteCode() +
                  '-' +
                  revision.number
                "
                pTooltip="Permanently delete this draft and return to the last published revision."
                tooltipPosition="top"
                (onClick)="confirmDiscardDraft(revision)"
              />
            }
          </div>
        </li>
      }
    </ul>
  } @else {
    <div class="py-8 text-center text-muted-color">
      <i class="pi pi-inbox mb-4 block text-4xl"></i>
      <p>No revisions found</p>
    </div>
  }
</p-card>

<p-dialog
  header="Request Changes"
  [visible]="requestChangesRevision() !== null"
  (visibleChange)="!$event && closeRequestChanges()"
  [modal]="true"
  [style]="{ width: '500px' }"
>
  <div class="flex flex-col gap-4">
    <p class="m-0 text-muted-color">
      Provide feedback on what changes are needed for this quote revision.
    </p>
    <textarea
      pTextarea
      rows="5"
      class="w-full"
      aria-label="Requested changes"
      placeholder="Enter your feedback here..."
      [value]="requestChangesNotes()"
      [disabled]="isBusy()"
      (input)="onRequestChangesInput($event)"
    ></textarea>
  </div>
  <ng-template #footer>
    <p-button
      label="Cancel"
      severity="secondary"
      [disabled]="isBusy()"
      (onClick)="closeRequestChanges()"
    />
    <p-button
      label="Submit"
      severity="warn"
      [disabled]="isBusy()"
      (onClick)="submitRequestChanges()"
    />
  </ng-template>
</p-dialog>
